/*
 * tps-brosurler — dil sekmeleri + gömülü görüntüleyici (eski hp-flipbook sekme davranışının kütüphanesiz karşılığı).
 * <tps-brosurler> içinde role=tablist/tab/tabpanel; sekmeler <a href="#panel"> (JS yoksa panele atlar).
 * - Tıklama/Enter/Space: sekmeyi seçer; ←/→/↑/↓ Home/End: odağı taşır (manuel etkinleştirme — her ok tuşunda iframe yüklenmesin).
 * - Yalnız seçili panelin iframe'i yüklenir: data-src → src. Yüklenince (load ya da 15 sn sonra) poster gizlenir.
 * - data-remember="true" ise seçim localStorage'da (data-storage-key) tutulur ve sonraki ziyarette geri yüklenir.
 * - Tema editöründe blok seçilince (shopify:block:select) ilgili sekme açılır.
 */
(() => {
  if (customElements.get('tps-brosurler')) return;

  const LOAD_TIMEOUT_MS = 15000;

  class TpsBrosurler extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.timers = new Map();
      if (!this.tablist || !this.tabs.length) return;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.tablist.addEventListener('click', this.onClick);
      this.tablist.addEventListener('keydown', this.onKeydown);
      this.panels.forEach((panel) => this.watchViewer(panel));

      let initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (this.remember) {
        const savedId = this.readSaved();
        const saved = savedId && this.tabs.find((tab) => tab.dataset.blockId === savedId);
        if (saved) initial = saved;
      }
      this.select(initial, { focus: false, persist: false });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this.tablist) {
        this.tablist.removeEventListener('click', this.onClick);
        this.tablist.removeEventListener('keydown', this.onKeydown);
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.timers.forEach((timer) => clearTimeout(timer));
      this.timers.clear();
    }

    get remember() {
      return this.dataset.remember === 'true';
    }

    get storageKey() {
      return this.dataset.storageKey || 'tps-brosurler-tab';
    }

    readSaved() {
      try {
        return window.localStorage.getItem(this.storageKey);
      } catch (error) {
        return null;
      }
    }

    writeSaved(blockId) {
      try {
        window.localStorage.setItem(this.storageKey, blockId);
      } catch (error) {
        /* gizli mod / depolama kapalı: sessizce geç */
      }
    }

    select(tab, { focus = false, persist = true } = {}) {
      if (!tab) return;
      const panelId = tab.getAttribute('aria-controls');
      this.tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
        item.classList.toggle('is-active', active);
      });
      this.panels.forEach((panel) => {
        const active = panel.id === panelId;
        panel.hidden = !active;
        if (active) this.loadViewer(panel);
      });
      if (focus) tab.focus();
      if (persist && this.remember && tab.dataset.blockId) this.writeSaved(tab.dataset.blockId);
    }

    loadViewer(panel) {
      const frame = panel.querySelector('iframe[data-src]');
      if (!frame) return;
      frame.src = frame.dataset.src;
      frame.removeAttribute('data-src');
      frame.removeAttribute('loading');
      this.startTimeout(panel);
    }

    watchViewer(panel) {
      const viewer = panel.querySelector('[data-tps-brosurler-viewer]');
      const frame = viewer && viewer.querySelector('iframe');
      if (!viewer || !frame) return;
      frame.addEventListener('load', () => {
        if (frame.getAttribute('src')) this.markLoaded(panel);
      });
      if (frame.getAttribute('src')) this.startTimeout(panel);
    }

    startTimeout(panel) {
      if (this.timers.has(panel)) return;
      this.timers.set(panel, setTimeout(() => this.markLoaded(panel), LOAD_TIMEOUT_MS));
    }

    markLoaded(panel) {
      const viewer = panel.querySelector('[data-tps-brosurler-viewer]');
      if (!viewer) return;
      viewer.classList.remove('is-loading');
      viewer.classList.add('is-loaded');
      const timer = this.timers.get(panel);
      if (timer) {
        clearTimeout(timer);
        this.timers.delete(panel);
      }
    }

    onClick(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !this.tabs.includes(tab)) return;
      event.preventDefault();
      this.select(tab, { focus: true });
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(document.activeElement);
      if (index < 0) return;
      const count = this.tabs.length;
      let next = null;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (index + 1) % count;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (index - 1 + count) % count;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = count - 1;
          break;
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          this.select(this.tabs[index], { focus: true });
          return;
        default:
          return;
      }
      event.preventDefault();
      this.tabs[next].focus();
    }

    onBlockSelect(event) {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      const tab = this.tabs.find((item) => item.dataset.blockId === blockId);
      if (tab) this.select(tab, { focus: false, persist: false });
    }
  }

  customElements.define('tps-brosurler', TpsBrosurler);
})();
